import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * What the interceptor needs from the host's auth stack. `shared/` knows no auth stack
 * (EAINT-954 Phase 4), so both halves are supplied by the host — Center-PWA wires them to
 * the Descope SDK in `DescopeApiClientsProvider`.
 */
export interface SessionRefreshOptions {
  /**
   * Renews the signed-in session through the identity provider. Resolves `true` once a
   * fresh credential is in place, `false` when the session can no longer be renewed (it was
   * revoked or has ended). Must not navigate or sign out: when the session has genuinely
   * ended the host's own session watcher (Center-PWA's `SessionEndGuard`) owns sign-out.
   */
  refreshSession: () => Promise<boolean>;
  /** The same per-request token source the client authenticates with. */
  tokenProvider: () => Promise<string | undefined>;
}

interface RequestConfigWithRefresh extends InternalAxiosRequestConfig {
  _sessionRefreshAttempted?: boolean;
}

/**
 * One renewal per burst, shared by every client built on the same `refreshSession`. A page
 * issues many requests at once; when the session token lapses they all 401 together, and
 * each starting its own renewal would race the identity provider for the same session.
 */
const refreshesInFlight = new WeakMap<SessionRefreshOptions['refreshSession'], Promise<boolean>>();

function renewOnce(refreshSession: SessionRefreshOptions['refreshSession']): Promise<boolean> {
  const inFlight = refreshesInFlight.get(refreshSession);
  if (inFlight) return inFlight;

  const attempt = (async () => {
    try {
      return await refreshSession();
    } catch {
      return false;
    } finally {
      refreshesInFlight.delete(refreshSession);
    }
  })();
  refreshesInFlight.set(refreshSession, attempt);
  return attempt;
}

/**
 * On a 401, renews the session once and replays the request once with the renewed token.
 *
 * A 401 from a Center service means the bearer was not accepted — usually a session token
 * that lapsed between the SDK's scheduled refreshes (a laptop waking, a throttled
 * background tab). Renewing and replaying recovers that transparently. When renewal fails,
 * or the replay is refused again, the original error is surfaced to the caller: this
 * interceptor never navigates and never clears credentials. Whether the session has ended
 * is the identity provider's call, and the host's session watcher acts on it.
 */
export function addTokenRefreshInterceptor(axiosInstance: AxiosInstance, options: SessionRefreshOptions): void {
  const { refreshSession, tokenProvider } = options;

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RequestConfigWithRefresh | undefined;

      if (!originalRequest || error.response?.status !== 401 || originalRequest._sessionRefreshAttempted) {
        throw error;
      }
      originalRequest._sessionRefreshAttempted = true;

      const renewed = await renewOnce(refreshSession);
      if (!renewed) {
        throw error;
      }

      const token = await tokenProvider();
      if (!token) {
        throw error;
      }
      originalRequest.headers.set('Authorization', `Bearer ${token}`);
      return axiosInstance(originalRequest);
    }
  );
}
